// PB-G1 — Pure display helpers for Powerball slips.
//
// Doctrine:
// - No fabrication. While a slip is partial, we say so honestly.
// - Combined odds = multiplicative product of leg odds. No sum, no rounding heuristics.
// - When the assembled-state odds fall outside the locked combined-odds range, the UI
//   shows "Rebalance pending" rather than the raw value (Agent 06 is already triggered
//   by the pipeline per AC-1.4).
//
// Consumed by PowerballPage.tsx and unit-tested in __tests__/slipDisplay.test.ts.

import {
  checkOddsRange,
  getRequiredLegCount,
  type SlipType,
} from "./ranges.ts";
import type { PbSlip, PbSelection } from "./usePowerballWeek.ts";

// ─── Combined odds ────────────────────────────────────────

/**
 * Multiplicative product of all leg odds. Returns 1 for zero-leg slips
 * (mathematical identity of the empty product) — callers should branch
 * on selection count rather than treating "1" as a real combined odds.
 */
export function combinedOddsOf(selections: readonly PbSelection[]): number {
  let product = 1;
  for (const s of selections) {
    product *= s.odds;
  }
  return product;
}

export type CombinedOddsKind = "building" | "in_range" | "out_of_range";

export interface CombinedOddsDisplay {
  kind: CombinedOddsKind;
  text: string;
  value?: number;
}

function isAssembledStatus(status: PbSlip["status"]): boolean {
  // 'assembled' / 'published' / 'settled' are all post-assembly states.
  // 'building', 'assembling', and 'rebalance_required' are not.
  return status === "assembled" || status === "published" || status === "settled";
}

// MM-DISPLAY (2026-05-29) — MIXED MEGA is an entertainment / repair-input
// product, not a band-locked Powerball. Founder ruling: "574K acca gives
// us enough room to use our SLIP EXTRACTOR and REPAIRER" — i.e. the
// combined and target-return MUST always render once legs complete,
// regardless of:
//   (a) whether the slip's persisted status is `rebalance_required`
//       (the assembler still bumps that flag when combined > 200K)
//   (b) whether combinedOddsOf is outside the 50K-200K target band
// Other slip types keep the existing band-locked behaviour: they're
// curated jackpot products where an out-of-band combined is a real
// rebalance signal, not user-facing variety.
function isMixedMega(slipType: string): boolean {
  return slipType === "mixed_mega_jackpot";
}

export function combinedOddsDisplay(slip: PbSlip): CombinedOddsDisplay {
  if (slip.publication_id && Number.isFinite(slip.combined_odds) && slip.combined_odds > 1) {
    return { kind: "in_range", text: `${formatOdds(slip.combined_odds)}×`, value: slip.combined_odds };
  }
  const required = getRequiredLegCount(slip.slip_type as SlipType);
  const legs = slip.selections.length;
  const mm = isMixedMega(slip.slip_type);

  // For MIXED MEGA, treat `rebalance_required` as 'assembled enough to
  // display' once leg count is met — the assembler has already done its
  // job; the user should see what they have. For other products keep
  // the strict isAssembledStatus gate.
  const assembled = mm
    ? legs >= required
    : isAssembledStatus(slip.status) && legs >= required;

  if (!assembled) {
    return {
      kind: "building",
      text: `Assembling — ${legs} of ${required} legs added.`,
    };
  }

  const value = combinedOddsOf(slip.selections);

  // MIXED MEGA: always show the numeric. Skip the range gate entirely.
  if (mm) {
    return { kind: "in_range", text: `${formatOdds(value)}×`, value };
  }

  const status = checkOddsRange(slip.slip_type as SlipType, value);
  if (status !== "in_range") {
    return { kind: "out_of_range", text: "Rebalance pending", value };
  }
  return { kind: "in_range", text: `${formatOdds(value)}×`, value };
}

function formatOdds(n: number): string {
  // No rounding heuristic — show one decimal for sub-100, integer otherwise,
  // both honest representations of the exact product.
  if (n >= 100) return Math.round(n).toLocaleString("en-GH");
  return n.toFixed(2);
}

// ─── Confidence + stake tier gating ───────────────────────

export interface GatedDisplay {
  text: string;
  ready: boolean;
}

export function confidenceDisplay(slip: PbSlip): GatedDisplay {
  const required = getRequiredLegCount(slip.slip_type as SlipType);
  const assembled = isAssembledStatus(slip.status) && slip.selections.length >= required;
  if (!assembled || slip.confidence_score === null || slip.confidence_score === undefined) {
    return { text: "Assembling — score available post-completion.", ready: false };
  }
  return { text: `${Math.round(slip.confidence_score)}%`, ready: true };
}

export function stakeTierDisplay(slip: PbSlip): GatedDisplay {
  const required = getRequiredLegCount(slip.slip_type as SlipType);
  const assembled = isAssembledStatus(slip.status) && slip.selections.length >= required;
  if (!assembled) {
    return { text: "Calculated post-assembly.", ready: false };
  }
  return { text: slip.stake_tier.replace("_", " "), ready: true };
}
